import {useState, useEffect} from "react";
import {useMutation} from "@apollo/client";
import {
    Backdrop,
    Button,
    CircularProgress,
    Dialog, DialogActions,
    DialogContent,
    DialogTitle,
    Typography
} from "@mui/material";

import {
    ALL_PERFORMERS_CONTENT_WITH_SERVICE_TYPE_AND_ACCREDITATION,
    CREATE_BULK_ACCREDITATION,
    DELETE_BULK_ACCREDITATION
} from "../../queries/GraphQLQueries";
import CustomAlert from "../CustomAlert";
import {NODE_LEVEL} from "../../domain/HierarchyConstants";
import {ACCREDITATION_ACTION_CREATE, ACCREDITATION_ACTION_REMOVE} from "../../views/AccreditationHierarchy";

export default function UpdateAccreditationDialog({contentInfoIds, domainUserId, domainUserName, accreditationAction, setAccreditationAction, nodeType, currentHierarchy, openUpdateAccreditationDialog, handleClose}) {

    const [alertMessage, setAlertMessage] = useState(null);
    const [title, setTitle] = useState("");
    const [isDataLoading, setIsDataLoading] = useState(false);
    const [message, setMessage] = useState("");
    const isUpdateForSingleContent = nodeType === NODE_LEVEL.CHILD;
    const isUpdateForContentFolder = nodeType === NODE_LEVEL.BRANCH;

    const [createBulkAccreditation, { }] = useMutation(CREATE_BULK_ACCREDITATION, {
        refetchQueries: [{ query: ALL_PERFORMERS_CONTENT_WITH_SERVICE_TYPE_AND_ACCREDITATION }, 'GetAllPerformersContentAndAccreditation'],
        awaitRefetchQueries: true,
        onCompleted: () => {
            setIsDataLoading(false);
        }
    });

    const [deleteBulkAccreditation, { }] = useMutation(DELETE_BULK_ACCREDITATION, {
        refetchQueries: [{ query: ALL_PERFORMERS_CONTENT_WITH_SERVICE_TYPE_AND_ACCREDITATION }, 'GetAllPerformersContentAndAccreditation'],
        awaitRefetchQueries: true,
        onCompleted: () => {
            setIsDataLoading(false);
        }
    });

    const handleCloseCustomAlert = () => setAlertMessage(null);

    const handleCreateBulkAccreditation = () => {
        setAlertMessage({children: 'Creating accreditations...', severity: 'success'});
        setIsDataLoading(true);

        createBulkAccreditation({
            variables: {contentInfoIds, domainUserId}
        }).then(() => handleClose())
        .catch((e) => {
            setAlertMessage({children: e?.message, severity: 'error'});
            return false;
        }).finally(() => setIsDataLoading(false));
    }

    const handleRemoveBulkAccreditation = () => {
        setIsDataLoading(true);
        setAlertMessage({children: 'Removing accreditations...', severity: 'success'});
        deleteBulkAccreditation({
            variables: {contentInfoIds, domainUserId}
        }).then(() => handleClose())
        .catch((e) => {
            setAlertMessage({children: e?.message, severity: 'error'});
            return false;
        }).finally(() => setIsDataLoading(false));
    }

    const handleAccreditationUpdate = () => {

        if (accreditationAction === ACCREDITATION_ACTION_CREATE) {
            handleCreateBulkAccreditation();
        } else if (accreditationAction === ACCREDITATION_ACTION_REMOVE) {
            handleRemoveBulkAccreditation();
        }
    }

    const backdropStyle = {
        color: '#fff',
        display: 'flex',
        zIndex: 1500
    };

    useEffect(() => {

        if (isUpdateForSingleContent) {
            handleAccreditationUpdate();
            setAccreditationAction(null);
        }

        const gainOrLoose = accreditationAction === ACCREDITATION_ACTION_CREATE ? 'gain' : 'loose';
        const accreditationCount = contentInfoIds.length;
        const message = `${domainUserName} will ${gainOrLoose} ${accreditationCount} accreditation${accreditationCount > 1 ? 's' : ''}${isUpdateForContentFolder ? ' under: ' : '.'}`;

        setMessage(message);
        setTitle(`${accreditationAction} Accreditation`);
    }, [nodeType, accreditationAction, contentInfoIds]);

    return (
        <>
            <Backdrop open={isDataLoading} sx={backdropStyle}>
                <CircularProgress color="inherit" />
            </Backdrop>

            {!isUpdateForSingleContent
                && <Dialog open={openUpdateAccreditationDialog}>
                    <DialogTitle>{title}</DialogTitle>
                    <DialogContent>
                        <Typography variant="body">{message}</Typography>
                        {isUpdateForContentFolder
                            && <Typography variant="body">
                                {currentHierarchy}
                            </Typography>
                        }
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => handleClose()}>Cancel</Button>
                        <Button onClick={handleAccreditationUpdate}>Proceed</Button>
                    </DialogActions>
                </Dialog>
            }

            <CustomAlert alertMessage={alertMessage} handleCloseCustomAlert={handleCloseCustomAlert} />
        </>
    );

}
